import { BookImageIcon } from 'lucide-react';
import React, { useState, useRef } from 'react';

interface FashionGalleryProps {
    index: number;
    images: string[];
    descriptions?: string[];
    notes?: (string | undefined)[];  // Add notes prop with optional handling for legacy compatibility
}

const FashionGallery: React.FC<FashionGalleryProps> = ({ index, images, descriptions, notes }) => {
    const finalDescriptions = descriptions || images.map((_, i) => `รูปที่ ${i + 1}`);
    // Handle notes with fallback for legacy compatibility
    const finalNotes = notes || images.map(() => undefined);
    const [currentIndex, setCurrentIndex] = useState(index);
    const startXRef = useRef<number | null>(null);
    const [isSwiping, setIsSwiping] = useState(false);
    const [translateX, setTranslateX] = useState(0);

    // Add zoom-related state
    const [scale, setScale] = useState(1);
    const [isPinching, setIsPinching] = useState(false);
    const [startDistance, setStartDistance] = useState(0);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const lastPositionRef = useRef({ x: 0, y: 0 });
    const imageRef = useRef<HTMLDivElement>(null);

    const goToNext = () => {
        if (scale > 1) return; // Prevent swiping when zoomed in
        setCurrentIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const goToPrevious = () => {
        if (scale > 1) return; // Prevent swiping when zoomed in
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const goToIndex = (index: number) => {
        setScale(1); // Reset zoom when changing images
        setPanPosition({ x: 0, y: 0 });
        setCurrentIndex(index);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        // Handle pinch to zoom (needs 2 touch points)
        if (e.touches.length === 2) {
            setIsPinching(true);
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            setStartDistance(distance);
            return;
        }

        // Handle single touch for panning (when zoomed) or swiping
        if (scale > 1) {
            // For panning when zoomed in
            lastPositionRef.current = {
                x: e.touches[0].clientX - panPosition.x,
                y: e.touches[0].clientY - panPosition.y
            };
        } else {
            // For swiping between images
            startXRef.current = e.touches[0].clientX;
            setIsSwiping(true);
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        // Handle pinch zoom
        if (e.touches.length === 2 && isPinching) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Calculate new scale based on finger distance change
            const newScale = Math.max(1, Math.min(4, scale * (distance / startDistance)));
            setScale(newScale);
            setStartDistance(distance);
            return;
        }

        // Handle panning when zoomed in
        if (scale > 1 && e.touches.length === 1) {
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;

            // Calculate max panning boundaries based on image size and zoom
            const maxPanX = (scale - 1) * (imageRef.current?.offsetWidth || 0) / 2;
            const maxPanY = (scale - 1) * (imageRef.current?.offsetHeight || 0) / 2;

            // Update pan position
            const newPanX = currentX - lastPositionRef.current.x;
            const newPanY = currentY - lastPositionRef.current.y;

            // Clamp pan values to stay within bounds
            const clampedX = Math.min(maxPanX, Math.max(-maxPanX, newPanX));
            const clampedY = Math.min(maxPanY, Math.max(-maxPanY, newPanY));

            setPanPosition({
                x: clampedX,
                y: clampedY
            });
            return;
        }

        // Handle swiping between images (only when not zoomed)
        if (!startXRef.current || scale > 1) return;

        const currentX = e.touches[0].clientX;
        const diffX = startXRef.current - currentX;
        setTranslateX(-diffX);

        // Don't navigate immediately on swipe - wait for touchEnd
        // This will make the swipe feel more natural with the animation
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (startXRef.current && scale <= 1) {
            const currentX = e.changedTouches[0].clientX;
            const diffX = startXRef.current - currentX;

            // Check if swipe was significant enough to change images
            if (Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    goToNext();
                } else {
                    goToPrevious();
                }
            }

            startXRef.current = null;
        }

        setIsSwiping(false);
        setIsPinching(false);
        setTranslateX(0);
    };

    const zoomIn = () => {
        setScale(prev => Math.min(prev + 0.5, 4));
    };

    const zoomOut = () => {
        setScale(prev => {
            const newScale = Math.max(prev - 0.5, 1);
            if (newScale === 1) {
                setPanPosition({ x: 0, y: 0 }); // Reset position when zooming out fully
            }
            return newScale;
        });
    };

    const resetZoom = () => {
        setScale(1);
        setPanPosition({ x: 0, y: 0 });
    };

    // Add mouse support for panning when zoomed in
    const handleMouseDown = (e: React.MouseEvent) => {
        if (scale > 1) {
            e.preventDefault();
            lastPositionRef.current = {
                x: e.clientX - panPosition.x,
                y: e.clientY - panPosition.y
            };
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (scale > 1) {
            const maxPanX = (scale - 1) * (imageRef.current?.offsetWidth || 0) / 2;
            const maxPanY = (scale - 1) * (imageRef.current?.offsetHeight || 0) / 2;

            const newPanX = e.clientX - lastPositionRef.current.x;
            const newPanY = e.clientY - lastPositionRef.current.y;

            const clampedX = Math.min(maxPanX, Math.max(-maxPanX, newPanX));
            const clampedY = Math.min(maxPanY, Math.max(-maxPanY, newPanY));

            setPanPosition({
                x: clampedX,
                y: clampedY
            });
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    return (
        <div className="max-w-2xl mx-auto p-4 relative">
            <div>
                <h2 className="text-xl font-semibold mb-2">{finalDescriptions[currentIndex]}</h2>
                <p className="text-gray-600 mb-2">
                    {images[currentIndex] ? `รูปที่ ${currentIndex + 1} จาก ${images.length}` : 'ไม่มีรูปภาพ'}
                </p>
            </div>
            {/* Main Image Container */}
            <div
                className={`flex flex-row mb-4 bg-gray-100 rounded-lg overflow-hidden relative transition-transform ${isSwiping ? 'duration-0' : 'duration-300'} touch-none`}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ transform: `translateX(${translateX}px)` }}
            >
                <div
                    ref={imageRef}
                    className="w-full relative"
                    style={{
                        minHeight: "300px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <img
                        src={images[currentIndex]}
                        className="object-contain max-h-[calc(100vh-200px)] sm:max-h-[calc(100vh-300px)] lg:max-h-[calc(100vh-400px)]"
                        style={{
                            transform: `scale(${scale}) translate(${panPosition.x / scale}px, ${panPosition.y / scale}px)`,
                            transformOrigin: "center",
                            transition: isPinching ? "none" : "transform 0.1s",
                            willChange: "transform",
                            cursor: scale > 1 ? 'grab' : 'auto',
                            imageRendering: scale > 1 ? 'crisp-edges' : 'auto', // Fixed: using valid ImageRendering value
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            // Add higher quality image rendering
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                            // Force hardware acceleration
                            WebkitTransform: `scale(${scale}) translate(${panPosition.x / scale}px, ${panPosition.y / scale}px)`,
                            WebkitTransformStyle: 'preserve-3d',
                            WebkitPerspective: '1000px'
                        }}
                        alt={`Image ${currentIndex + 1}`}
                        onDoubleClick={scale > 1 ? resetZoom : zoomIn}
                        onMouseDown={handleMouseDown}
                        draggable="false"
                        loading="eager"
                    />
                </div>

                {/* Navigation Arrows (only visible when not zoomed) */}
                {scale <= 1 && (
                    <>
                        <button
                            onClick={goToPrevious}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all z-10"
                            aria-label="Previous image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <button
                            onClick={goToNext}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all z-10"
                            aria-label="Next image"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Zoom Controls */}
                <div className="absolute bottom-2 right-2 flex gap-2 z-10">
                    <button
                        onClick={zoomOut}
                        disabled={scale <= 1}
                        className={`p-2 rounded-full bg-white/80 hover:bg-white shadow-lg ${scale <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label="Zoom out"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                    </button>
                    {scale > 1 && (
                        <button
                            onClick={resetZoom}
                            className="p-2 rounded-full bg-white/80 hover:bg-white shadow-lg"
                            aria-label="Reset zoom"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11 4v4m0 0h-4m4 0l-5-5" />
                            </svg>
                        </button>
                    )}
                    <button
                        onClick={zoomIn}
                        disabled={scale >= 4}
                        className={`p-2 rounded-full bg-white/80 hover:bg-white shadow-lg ${scale >= 4 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        aria-label="Zoom in"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Display notes if available */}
            {finalNotes && finalNotes[currentIndex] && finalNotes[currentIndex]?.trim() && (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <BookImageIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700 font-medium">หมายเหตุ:</p>
                            <p className="text-sm text-blue-600 whitespace-pre-wrap">{finalNotes[currentIndex]}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Thumbnails */}
            <div className="hidden sm:grid grid-cols-10 gap-2">
                {images.map((image, index) => (
                    <button
                        key={index}
                        onClick={() => goToIndex(index)}
                        className={`aspect-square rounded-lg overflow-hidden ${currentIndex === index ? 'ring-2 ring-black' : ''
                            } hover:ring-2`}
                    >
                        <img
                            src={image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                        />
                    </button>
                ))}
            </div>
        </div>
    );
};

export default FashionGallery;